import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db, firestorePathId } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

const FEATURE_FLAG_PATH = `artifacts/${firestorePathId}/public/data/featureFlags/entendaCalculo`;

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '';
};

const normalizeMunicipioIds = (config) => {
  if (!config) return [];

  if (Array.isArray(config.municipioIds)) {
    return config.municipioIds.map((id) => String(id || '').trim()).filter(Boolean);
  }

  if (Array.isArray(config.municipiosLiberados)) {
    return config.municipiosLiberados.map((id) => String(id || '').trim()).filter(Boolean);
  }

  return [];
};

const isEnabledByMasterConfig = (config, municipioId) => {
  if (!config || !municipioId) return false;

  const normalizedMunicipioId = String(municipioId || '').trim();
  if (!normalizedMunicipioId) return false;

  const status = String(config.status || config.modo || '').toLowerCase();
  const municipioIds = normalizeMunicipioIds(config);

  if (status === 'all' || status === 'todos' || config.enabledAll === true) {
    return true;
  }

  if (status === 'all_except' || status === 'todos_exceto' || status === 'except') {
    return !municipioIds.includes(normalizedMunicipioId);
  }

  if (status === 'selected' || status === 'selecionados' || status === 'municipios') {
    return municipioIds.includes(normalizedMunicipioId);
  }

  return false;
};

export const useEntendaCalculoAccess = () => {
  const { user, isAuthenticated } = useAuth();
  const municipioId = user?.municipioId || '';
  const localOverride = useMemo(() => isLocalhost(), []);
  const [state, setState] = useState({
    loading: !localOverride,
    config: null,
    error: null,
  });

  useEffect(() => {
    if (localOverride) {
      setState({
        loading: false,
        config: { status: 'localhost' },
        error: null,
      });
      return undefined;
    }

    if (!isAuthenticated || !municipioId) {
      setState({
        loading: false,
        config: null,
        error: null,
      });
      return undefined;
    }

    setState((current) => ({ ...current, loading: true, error: null }));

    const flagRef = doc(db, FEATURE_FLAG_PATH);

    return onSnapshot(flagRef, (snapshot) => {
      setState({
        loading: false,
        config: snapshot.exists() ? snapshot.data() : null,
        error: null,
      });
    }, (error) => {
      console.error('Erro ao carregar controle do Entenda seu cálculo:', error);
      setState({
        loading: false,
        config: null,
        error,
      });
    });
  }, [isAuthenticated, localOverride, municipioId]);

  const enabledByMaster = useMemo(
    () => isEnabledByMasterConfig(state.config, municipioId),
    [municipioId, state.config]
  );

  const enabled = localOverride || enabledByMaster;

  return {
    enabled,
    loading: localOverride ? false : state.loading,
    source: localOverride ? 'localhost' : (enabledByMaster ? 'master' : 'disabled'),
    config: state.config,
    error: state.error,
  };
};

export default useEntendaCalculoAccess;
